import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Avatar,
  Divider,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { supabase } from '../config/supabase';
import { lookupLabel } from '../data/lookups';
import { backToBackMatchDayToleranceOptions, sexOptions, singlesPreferenceOptions, usStateOptions } from '../data/options';
import type { EntityConfig, FieldConfig, LookupState, RowRecord, SortDirection, SortRule, UserPreferences } from '../types';
import { comparePlayerNames, formatPlayerName } from '../utils/playerPreferences';
import { teamAccentColor } from '../utils/teamColors';
import { feeAssignmentSyncErrorMessage, syncTeamFeeAssignments } from '../utils/feeAssignments';
import { PlayerDisplayControls } from './PlayerDisplayControls';
import { useAuditUserId } from '../auth/AuthContext';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { formatEdr } from '../utils/edr';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { DraftSavedIndicator } from './DraftSavedIndicator';
import { DialogActionBar } from './ux/DialogActionBar';
import { EmptyState } from './ux/SemanticState';
import { PlayerWelcomeEmailPanel } from './PlayerWelcomeEmailPanel';
import {
  googleMapsSearchUrl,
  parseSafeUrl,
  validateFacilityForm,
  validatePlayerForm,
  type ValidationMessage,
} from '../utils/dataQualityValidation';

type CrudPageProps = {
  entity: EntityConfig;
  lookups: LookupState;
  refreshLookups: () => Promise<void>;
  userPreferences: UserPreferences;
  onUserPreferencesChange?: (patch: Partial<UserPreferences>) => void;
  readOnly?: boolean;
};

type Snack = {
  message: string;
  severity: 'success' | 'error';
};

type PlayerCreditBalance = {
  workspace_id: string;
  player_id: string;
  credit_balance: number;
  last_activity_at: string | null;
};

type PlayerLedgerEntry = {
  id: string;
  workspace_id: string;
  player_id: string;
  transaction_type: string;
  amount: number;
  description: string;
  source_fee_campaign_id: string | null;
  destination_fee_campaign_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
};

type PlayerRosterAssignment = {
  id: string;
  team_id: string;
  player_id: string;
  roster_status: string | null;
  roster_role: string | null;
  target_match_count: number | null;
  player_tier: string | null;
  is_active: boolean;
  teams?: { id: string; is_active: boolean | null; deleted_at: string | null } | null;
};

type PlayerQuickFilter = 'all' | 'needs_attention' | 'missing_ratings' | 'not_linked' | 'not_assigned' | 'linked';
type PlayerDrawerTab = 'overview' | 'teams' | 'history';
type CrudViewState = {
  facilitySearch: string;
  playerSearch: string;
  playerSexFilter: string;
  playerNtrpFilter: string;
  playerActiveFilter: string;
  playerQuickFilter: PlayerQuickFilter;
  playerDrawerId: string | null;
  playerDrawerTab: PlayerDrawerTab;
  activeSort: {
    field: string | null;
    direction: SortDirection;
  };
};

const facilitySearchColumns = ['facility_name', 'city', 'state', 'zip_code'];
const playerGridFields = [
  'first_name',
  'last_name',
  'sex',
  'ntrp_rating',
  'estimated_dynamic_rating',
  'utr_singles_rating',
  'utr_doubles_rating',
  'email',
  'mobile_phone',
  'birthday',
  'usta_profile_url',
  'utr_profile_url',
  'tennis_record_profile_url',
  'is_active',
  'notes',
] as const;
const playerGridLabels: Record<(typeof playerGridFields)[number], string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  sex: 'Gender',
  ntrp_rating: 'NTRP',
  estimated_dynamic_rating: 'EDR',
  utr_singles_rating: 'Singles UTR',
  utr_doubles_rating: 'Doubles UTR',
  email: 'Email',
  mobile_phone: 'Mobile Phone',
  birthday: 'Birthday',
  usta_profile_url: 'USTA Profile URL',
  utr_profile_url: 'UTR Profile URL',
  tennis_record_profile_url: 'TennisRecord Profile URL',
  is_active: 'Active',
  notes: 'Notes',
};
const ntrpOptions = ['', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '7.0'];

function money(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function emptyValue(field: FieldConfig): string | number | boolean | null {
  if (field.type === 'boolean') return false;
  if (field.type === 'number') return null;
  return '';
}

function initialForm(entity: EntityConfig, row?: RowRecord): RowRecord {
  return entity.fields.reduce<RowRecord>((acc, field) => {
    const existing = row?.[field.name];
    if (existing !== undefined && existing !== null) {
      acc[field.name] = existing;
    } else if (field.name === 'is_active' || field.name === 'active_status' || field.name === 'counts_toward_fairness' || field.name === 'player_portal_access_enabled') {
      acc[field.name] = true;
    } else if (field.name === 'required_male_count' || field.name === 'required_female_count' || field.name === 'target_match_count') {
      acc[field.name] = 0;
    } else if (field.name === 'role' || field.name === 'assignment_role') {
      acc[field.name] = 'captain';
    } else if (field.name === 'roster_status') {
      acc[field.name] = 'active';
    } else if (field.name === 'team_gender_type') {
      acc[field.name] = 'mixed';
    } else if (field.name === 'advancement_type') {
      acc[field.name] = 'advancing';
    } else if (field.name === 'player_rating_basis') {
      acc[field.name] = 'edr';
    } else if (field.name === 'team_color') {
      acc[field.name] = 'gray';
    } else if (field.name === 'default_warmup_minutes') {
      acc[field.name] = 30;
    } else if (field.name === 'workspace_id' && entity.key === 'teams') {
      acc[field.name] = '';
    } else {
      acc[field.name] = emptyValue(field);
    }
    return acc;
  }, {});
}

function normalizeForm(entity: EntityConfig, form: RowRecord): RowRecord {
  const payload: RowRecord = {};
  for (const field of entity.fields) {
    const value = form[field.name];
    if (field.type === 'number') {
      payload[field.name] = value === '' || value === null || value === undefined ? null : Number(value);
    } else if (field.type === 'boolean') {
      payload[field.name] = Boolean(value);
    } else if (field.name === 'team_flight') {
      payload[field.name] = typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
    } else {
      payload[field.name] = value === '' ? null : value;
    }
  }
  return payload;
}

function isValidUrl(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidEmail(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isOptionalNumber(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  return Number.isFinite(Number(value));
}

function normalizeGridValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function normalizeEmailValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
}

function normalizeNtrpValue(value: unknown): string {
  if (value === '' || value === null || value === undefined) return '';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(1) : String(value);
}

function normalizeGridValueForField(field: string, value: unknown): string {
  if (field === 'email') return normalizeEmailValue(value);
  if (field === 'ntrp_rating') return normalizeNtrpValue(value);
  if (field === 'estimated_dynamic_rating') return formatEdr(value);
  return normalizeGridValue(value);
}

function gridPayloadValue(field: string, value: unknown): string | number | boolean | null {
  if (field === 'is_active') return Boolean(value);
  if (['ntrp_rating', 'estimated_dynamic_rating', 'utr_singles_rating', 'utr_doubles_rating'].includes(field)) {
    return value === '' || value === null || value === undefined ? null : Number(value);
  }
  if (field === 'email') {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    return trimmed === '' ? null : (trimmed as string | number | boolean | null);
  }
  return value === '' ? null : (value as string | number | boolean | null);
}

function friendlySaveError(message: string): string {
  if (message.includes('players_email_unique_when_present_idx')) {
    return 'Another player already uses one of those email addresses.';
  }
  return message;
}

function isOptionalNonNegativeInteger(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue >= 0;
}

async function validateRosterGender(teamId: unknown, playerId: unknown): Promise<string | null> {
  if (typeof teamId !== 'string' || typeof playerId !== 'string' || !teamId || !playerId) return null;
  const [{ data: team, error: teamError }, { data: player, error: playerError }] = await Promise.all([
    supabase.from('teams').select('team_gender_type').eq('id', teamId).maybeSingle(),
    supabase.from('players').select('sex').eq('id', playerId).maybeSingle(),
  ]);
  if (teamError) return teamError.message;
  if (playerError) return playerError.message;

  const teamGenderType = (team as { team_gender_type?: string } | null)?.team_gender_type || 'mixed';
  const playerSex = (player as { sex?: string } | null)?.sex;
  if (teamGenderType === 'mens' && playerSex !== 'male') return "Men's teams can only add male players.";
  if (teamGenderType === 'womens' && playerSex !== 'female') return "Women's teams can only add female players.";
  return null;
}

function profileUrl(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function CrudPage({ entity, lookups, refreshLookups, userPreferences, onUserPreferencesChange, readOnly = false }: CrudPageProps) {
  const auditUserId = useAuditUserId();
  const defaultViewState = useMemo<CrudViewState>(() => ({
    facilitySearch: '',
    playerSearch: '',
    playerSexFilter: '',
    playerNtrpFilter: '',
    playerActiveFilter: 'active',
    playerQuickFilter: 'all',
    playerDrawerId: null,
    playerDrawerTab: 'overview',
    activeSort: { field: null, direction: 'asc' },
  }), []);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<CrudViewState>({
    userId: auditUserId,
    routeKey: entity.key,
    defaultState: defaultViewState,
  });
  const [rows, setRows] = useState<RowRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RowRecord | null>(null);
  const [form, setForm] = useState<RowRecord>(() => initialForm(entity));
  const [savedForm, setSavedForm] = useState<RowRecord>(() => initialForm(entity));
  const [facilitySearch, setFacilitySearch] = useState(savedViewState.facilitySearch);
  const [playerSearch, setPlayerSearch] = useState(savedViewState.playerSearch);
  const [playerSexFilter, setPlayerSexFilter] = useState(savedViewState.playerSexFilter);
  const [playerNtrpFilter, setPlayerNtrpFilter] = useState(savedViewState.playerNtrpFilter);
  const [playerActiveFilter, setPlayerActiveFilter] = useState(savedViewState.playerActiveFilter);
  const [playerQuickFilter, setPlayerQuickFilter] = useState<PlayerQuickFilter>(savedViewState.playerQuickFilter);
  const [playerRosterAssignments, setPlayerRosterAssignments] = useState<PlayerRosterAssignment[]>([]);
  const [playerDrawerId, setPlayerDrawerId] = useState<string | null>(savedViewState.playerDrawerId);
  const [playerDrawerTab, setPlayerDrawerTab] = useState<PlayerDrawerTab>(savedViewState.playerDrawerTab);
  const [playerActionAnchor, setPlayerActionAnchor] = useState<HTMLElement | null>(null);
  const [playerActionRowId, setPlayerActionRowId] = useState<string | null>(null);
  const [playerViewAnchor, setPlayerViewAnchor] = useState<HTMLElement | null>(null);
  const [playerMoreFilterAnchor, setPlayerMoreFilterAnchor] = useState<HTMLElement | null>(null);
  const [activeSort, setActiveSort] = useState<{ field: string | null; direction: SortDirection }>(savedViewState.activeSort);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [gridEditMode, setGridEditMode] = useState(false);
  const [gridDrafts, setGridDrafts] = useState<Record<string, RowRecord>>({});
  const [gridErrors, setGridErrors] = useState<Record<string, string>>({});
  const [syncingTennisRecordPlayerIds, setSyncingTennisRecordPlayerIds] = useState<Set<string>>(() => new Set());
  const [financialPlayer, setFinancialPlayer] = useState<RowRecord | null>(null);
  const [financialBalances, setFinancialBalances] = useState<PlayerCreditBalance[]>([]);
  const [financialLedger, setFinancialLedger] = useState<PlayerLedgerEntry[]>([]);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formWarnings, setFormWarnings] = useState<Record<string, string[]>>({});
  const [globalWarnings, setGlobalWarnings] = useState<ValidationMessage[]>([]);
  const [saveWarningsAccepted, setSaveWarningsAccepted] = useState(false);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  const title = entity.title;
  const isFacilities = entity.key === 'facilities';
  const isPlayers = entity.key === 'players';
  const singularEntityTitle = isPlayers ? 'Player' : isFacilities ? 'Facility' : title.replace(/s$/, '');
  const primaryAddLabel = `Add ${singularEntityTitle}`;
  const commitLabel = saving
    ? 'Saving...'
    : editing
      ? 'Save Changes'
      : `Create ${singularEntityTitle}`;
  const formDraftPersistence = useDraftPersistence({
    userId: auditUserId,
    screenType: isFacilities ? 'facility-edit' : 'player-edit',
    entityId: editing?.id ? String(editing.id) : 'new',
    value: form,
    initialValue: savedForm,
    enabled: dialogOpen && (isPlayers || isFacilities) && !readOnly,
  });
  const sortableColumns = useMemo(() => new Set(entity.sortableColumns || []), [entity.sortableColumns]);

  const resetValidation = () => {
    setFormErrors({});
    setFormWarnings({});
    setGlobalWarnings([]);
    setSaveWarningsAccepted(false);
  };

  const updateFormField = (field: string, value: string | boolean | number | null) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setSaveWarningsAccepted(false);
  };

  const applyValidationMessages = (errors: ValidationMessage[], warnings: ValidationMessage[]) => {
    setFormErrors(errors.reduce<Record<string, string>>((acc, item) => {
      if (item.field && !acc[item.field]) acc[item.field] = item.message;
      return acc;
    }, {}));
    setFormWarnings(warnings.reduce<Record<string, string[]>>((acc, item) => {
      if (item.field) acc[item.field] = [...(acc[item.field] || []), item.message];
      return acc;
    }, {}));
    setGlobalWarnings(warnings);
  };

  const normalizeFieldOnBlur = (field: string) => {
    if (!isPlayers && !isFacilities) return;
    const validation = isPlayers
      ? validatePlayerForm(form, { existingRows: rows, currentId: editing?.id ? String(editing.id) : null })
      : validateFacilityForm(form, { existingRows: rows, currentId: editing?.id ? String(editing.id) : null });
    if (validation.normalized[field] !== undefined) {
      setForm((current) => ({ ...current, [field]: validation.normalized[field] }));
    }
  };

  const loadRows = useCallback(async () => {
    setLoading(true);
    const query = supabase.from(entity.table).select('*').is('deleted_at', null);
    if (isFacilities) {
      query.order('state', { ascending: true }).order('city', { ascending: true }).order('facility_name', { ascending: true });
    } else if (entity.orderBy) {
      query.order(entity.orderBy, { ascending: true });
    }
    const { data, error } = await query;
    setLoading(false);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setRows((data || []) as RowRecord[]);
    if (isPlayers) {
      const rosterResult = await supabase
        .from('team_rosters')
        .select('id, team_id, player_id, roster_status, roster_role, target_match_count, player_tier, is_active, teams!inner(id, is_active, deleted_at)')
        .eq('is_active', true)
        .is('deleted_at', null)
        .eq('teams.is_active', true)
        .is('teams.deleted_at', null);
      if (rosterResult.error) {
        setSnack({ severity: 'error', message: rosterResult.error.message });
        setPlayerRosterAssignments([]);
      } else {
        setPlayerRosterAssignments((rosterResult.data || []) as unknown as PlayerRosterAssignment[]);
      }
    } else {
      setPlayerRosterAssignments([]);
    }
  }, [entity, isFacilities, isPlayers]);

  useEffect(() => {
    setEditing(null);
    setForm(initialForm(entity));
    setGridEditMode(false);
    setGridDrafts({});
    setGridErrors({});
    void loadRows();
  }, [entity, loadRows]);

  const openCreate = () => {
    if (readOnly) return;
    if (gridEditMode && Object.keys(gridDrafts).length > 0 && !window.confirm('You have unsaved player edits. Discard changes?')) return;
    setGridEditMode(false);
    setGridDrafts({});
    setGridErrors({});
    setEditing(null);
    const nextForm = initialForm(entity);
    if (entity.key === 'players') {
      nextForm.is_active = true;
      nextForm.singles_preference = nextForm.singles_preference || 'if_possible';
      nextForm.back_to_back_match_day_tolerance = nextForm.back_to_back_match_day_tolerance || 'if_needed';
      nextForm.same_day_match_tolerance = nextForm.same_day_match_tolerance || 'if_needed';
      nextForm.minimum_recovery_hours = nextForm.minimum_recovery_hours ?? 16;
    }
    if (entity.key === 'teams' && lookups.workspaces.length === 1) {
      nextForm.workspace_id = lookups.workspaces[0].value;
    }
    setForm(nextForm);
    setSavedForm(nextForm);
    resetValidation();
    setDialogOpen(true);
  };

  const openEdit = (row: RowRecord) => {
    if (readOnly) return;
    if (gridEditMode && Object.keys(gridDrafts).length > 0 && !window.confirm('You have unsaved player edits. Discard changes?')) return;
    setGridEditMode(false);
    setGridDrafts({});
    setGridErrors({});
    setEditing(row);
    const nextForm = initialForm(entity, row);
    setForm(nextForm);
    setSavedForm(nextForm);
    resetValidation();
    setDialogOpen(true);
  };

  const playerDeactivationWarnings = async (playerId: string): Promise<ValidationMessage[]> => {
    const today = new Date().toISOString().slice(0, 10);
    const [rosters, lineups, standby] = await Promise.all([
      supabase.from('team_rosters').select('id').eq('player_id', playerId).eq('is_active', true).is('deleted_at', null),
      supabase.from('lineup_entries').select('id, match_events!inner(match_date, match_status)').eq('player_id', playerId).gte('match_events.match_date', today).is('deleted_at', null),
      supabase.from('standby_players').select('id, match_events!inner(match_date, match_status)').eq('player_id', playerId).gte('match_events.match_date', today).is('deleted_at', null),
    ]);
    const warnings: ValidationMessage[] = [];
    const rosterCount = rosters.data?.length || 0;
    const lineupCount = lineups.data?.length || 0;
    const standbyCount = standby.data?.length || 0;
    if (rosterCount || lineupCount || standbyCount) {
      warnings.push({
        severity: 'warning',
        message: `This player is on ${rosterCount} active roster${rosterCount === 1 ? '' : 's'}, assigned to ${lineupCount} future lineup slot${lineupCount === 1 ? '' : 's'}, and listed as standby ${standbyCount} time${standbyCount === 1 ? '' : 's'}. Historical records will remain intact, but the player will no longer appear in normal active-player selectors.`,
      });
    }
    return warnings;
  };

  const facilityDeactivationWarnings = async (facilityId: string): Promise<ValidationMessage[]> => {
    const today = new Date().toISOString().slice(0, 10);
    const [teams, matches, events] = await Promise.all([
      supabase.from('teams').select('id').eq('home_facility_id', facilityId).eq('is_active', true).is('deleted_at', null),
      supabase.from('match_events').select('id').eq('facility_id', facilityId).gte('match_date', today).is('deleted_at', null),
      supabase.from('team_events').select('id').eq('facility_id', facilityId).gte('event_date', today).is('deleted_at', null),
    ]);
    const teamCount = teams.data?.length || 0;
    const matchCount = matches.data?.length || 0;
    const eventCount = events.data?.length || 0;
    return teamCount || matchCount || eventCount ? [{
      severity: 'warning',
      message: `This facility is used by ${teamCount} active team${teamCount === 1 ? '' : 's'}, ${matchCount} future match${matchCount === 1 ? '' : 'es'}, and ${eventCount} future team event${eventCount === 1 ? '' : 's'}. Historical records will remain intact, but it will no longer appear in default selectors for new records.`,
    }] : [];
  };

  const closeDialog = () => {
    if (!dialogOpen) return;
    const hasUnsavedChanges = JSON.stringify(form) !== JSON.stringify(savedForm);
    if (hasUnsavedChanges && !window.confirm('Discard unsaved changes?')) return;
    setDialogOpen(false);
    resetValidation();
  };

  const save = async (options: { acceptWarnings?: boolean } = {}) => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (saving) return;
    setSaving(true);
    if (!isPlayers && !isFacilities) {
      const missingRequiredField = entity.fields.find((field) => field.required && (form[field.name] === '' || form[field.name] === null || form[field.name] === undefined));
      if (missingRequiredField) {
        setSnack({ severity: 'error', message: `${missingRequiredField.label} is required.` });
        setSaving(false);
        return;
      }

      const invalidUrlField = entity.fields.find((field) => field.type === 'url' && !isValidUrl(form[field.name]));
      if (invalidUrlField) {
        setSnack({ severity: 'error', message: `${invalidUrlField.label} must be a valid URL.` });
        setSaving(false);
        return;
      }
    }

    if (entity.table === 'teams' && !isOptionalNonNegativeInteger(form.default_target_match_count_per_player)) {
      setSnack({ severity: 'error', message: 'Default Target Matches Per Player must be a whole number zero or greater.' });
      setSaving(false);
      return;
    }
    if (entity.table === 'teams' && !isOptionalNonNegativeInteger(form.default_warmup_minutes)) {
      setSnack({ severity: 'error', message: 'Default Warmup Minutes must be a whole number zero or greater.' });
      setSaving(false);
      return;
    }
    if (entity.table === 'teams' && form.default_warmup_minutes !== '' && form.default_warmup_minutes !== null && Number(form.default_warmup_minutes) > 120) {
      setSnack({ severity: 'error', message: 'Default Warmup Minutes should be 120 or less.' });
      setSaving(false);
      return;
    }
    if (entity.table === 'teams' && typeof form.team_flight === 'string' && form.team_flight.trim().length > 30) {
      setSnack({ severity: 'error', message: 'TCD Flight must be 30 characters or fewer.' });
      setSaving(false);
      return;
    }

    let validatedForm = form;
    if (entity.table === 'players' || entity.table === 'facilities') {
      const validation = entity.table === 'players'
        ? validatePlayerForm(form, { existingRows: rows, currentId: editing?.id ? String(editing.id) : null })
        : validateFacilityForm(form, { existingRows: rows, currentId: editing?.id ? String(editing.id) : null });
      let warnings = validation.warnings;
      if (editing?.id && editing.is_active !== false && validation.normalized.is_active === false) {
        warnings = [
          ...warnings,
          ...(entity.table === 'players'
            ? await playerDeactivationWarnings(String(editing.id))
            : await facilityDeactivationWarnings(String(editing.id))),
        ];
      }
      setForm(validation.normalized);
      applyValidationMessages(validation.errors, warnings);
      if (validation.errors.length > 0) {
        setSnack({ severity: 'error', message: 'Fix the highlighted fields before saving.' });
        setSaving(false);
        return;
      }
      if (warnings.length > 0 && !saveWarningsAccepted && !options.acceptWarnings) {
        setSnack({ severity: 'error', message: 'Review the warnings, then choose Save Anyway if they are acceptable.' });
        setSaving(false);
        return;
      }
      validatedForm = validation.normalized;
    }

    const payload = normalizeForm(entity, validatedForm);
    if (entity.table === 'players') {
      payload.singles_preference = payload.singles_preference || 'if_possible';
      payload.back_to_back_match_day_tolerance = payload.back_to_back_match_day_tolerance || 'if_needed';
      payload.same_day_match_tolerance = payload.same_day_match_tolerance || 'if_needed';
      payload.minimum_recovery_hours = payload.minimum_recovery_hours === '' || payload.minimum_recovery_hours === null
        ? 16
        : Number(payload.minimum_recovery_hours);
      if (!Number.isFinite(payload.minimum_recovery_hours as number) || Number(payload.minimum_recovery_hours) < 0 || Number(payload.minimum_recovery_hours) > 72) {
        setSnack({ severity: 'error', message: 'Minimum Recovery Hours must be between 0 and 72.' });
        setSaving(false);
        return;
      }
      if (!isOptionalNumber(payload.estimated_dynamic_rating)) {
        setSnack({ severity: 'error', message: 'Estimated Dynamic Rating (EDR) must be numeric.' });
        setSaving(false);
        return;
      }
      if (payload.estimated_dynamic_rating !== null && payload.estimated_dynamic_rating !== undefined && (Number(payload.estimated_dynamic_rating) < 1 || Number(payload.estimated_dynamic_rating) > 7)) {
        setSnack({ severity: 'error', message: 'Estimated Dynamic Rating (EDR) must be between 1.00 and 7.00.' });
        setSaving(false);
        return;
      }
      const previousEdr = editing?.estimated_dynamic_rating ?? null;
      const nextEdr = payload.estimated_dynamic_rating ?? null;
      if (Number(previousEdr) !== Number(nextEdr) || (previousEdr === null) !== (nextEdr === null)) {
        payload.estimated_dynamic_rating_updated_at = nextEdr === null ? null : new Date().toISOString();
      }
    }
    if (entity.table === 'team_rosters' && payload.is_active !== false && payload.deleted_at === undefined) {
      const rosterGenderError = await validateRosterGender(payload.team_id, payload.player_id);
      if (rosterGenderError) {
        setSnack({ severity: 'error', message: rosterGenderError });
        setSaving(false);
        return;
      }
    }

    const { data: savedPlayer, error } = entity.table === 'players'
      ? editing?.id
        ? await supabase.from(entity.table).update(payload).eq('id', editing.id).select('id,email').single()
        : await supabase.from(entity.table).insert(payload).select('id,email').single()
      : editing?.id
        ? await supabase.from(entity.table).update(payload).eq('id', editing.id)
        : await supabase.from(entity.table).insert(payload);
    if (error) {
      setSnack({ severity: 'error', message: friendlySaveError(error.message) });
      setSaving(false);
      return;
    }

    let successMessage = `${title.slice(0, -1) || title} saved.`;
    if (entity.table === 'players' && savedPlayer?.id && typeof savedPlayer.email === 'string' && savedPlayer.email.trim()) {
      try {
        const session = (await supabase.auth.getSession()).data.session;
        const response = await fetch('/api/auth/ensure-player-account', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ playerId: savedPlayer.id }),
        });
        const result = await response.json().catch(() => ({})) as { error?: string; authLinked?: boolean; skipped?: boolean; reason?: string };
        if (!response.ok || result.error) {
          successMessage = `Player saved, but login account setup failed: ${result.error || response.statusText}`;
        } else if (result.authLinked && !result.skipped) {
          successMessage = 'Player saved and login account is ready.';
        } else if (result.reason === 'email_change_verification_required') {
          successMessage = 'Player saved. The player must verify the new sign-in email from Account Settings before it takes effect.';
        }
      } catch (accountError) {
        successMessage = `Player saved, but login account setup failed: ${accountError instanceof Error ? accountError.message : 'Unknown error.'}`;
      }
    }
    if (entity.table === 'players' && savedPlayer?.id && typeof savedPlayer.email === 'string' && savedPlayer.email.trim()) {
      const previousEmail = typeof editing?.email === 'string' ? editing.email.trim() : '';
      const triggerSource = !editing?.id
        ? 'automatic_new_player'
        : !previousEmail
          ? 'automatic_first_email'
          : null;
      if (triggerSource) {
        try {
          const session = (await supabase.auth.getSession()).data.session;
          const response = await fetch('/api/welcome-email/queue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}) },
            body: JSON.stringify({ playerId: savedPlayer.id, triggerSource }),
          });
          const result = await response.json().catch(() => ({})) as { queued?: boolean; reason?: string; error?: string };
          if (!response.ok) successMessage = `${successMessage} Welcome email could not be queued: ${result.error || response.statusText}`;
          else if (result.queued) successMessage = `${successMessage} Welcome email queued.`;
        } catch (welcomeError) {
          successMessage = `${successMessage} Welcome email could not be queued: ${welcomeError instanceof Error ? welcomeError.message : 'Unknown error.'}`;
        }
      }
    }
    if (!editing?.id && entity.table === 'team_rosters' && payload.is_active !== false && payload.team_id && payload.player_id) {
      try {
        const feeAssignmentCount = await syncTeamFeeAssignments(String(payload.team_id), [String(payload.player_id)], undefined, auditUserId);
        if (feeAssignmentCount > 0) {
          successMessage = `${feeAssignmentCount} active fee assignment${feeAssignmentCount === 1 ? '' : 's'} created.`;
        }
      } catch (syncError) {
        successMessage = `Roster saved, but fee assignment sync failed: ${feeAssignmentSyncErrorMessage(syncError)}`;
      }
    }

    setDialogOpen(false);
    setSavedForm(validatedForm);
    formDraftPersistence.clearDraft();
    resetValidation();
    setSnack({ severity: successMessage.includes('failed') ? 'error' : 'success', message: successMessage });
    await loadRows();
    await refreshLookups();
    setSaving(false);
  };

  const softDelete = async (row: RowRecord) => {
    if (readOnly) return;
    const label = row.name || row.team_name || row.season_name || row.facility_name || row.id;
    if (!window.confirm(`Delete ${singularEntityTitle}?\n\nThis will remove ${label} from active CourtCaptain views. Historical references remain according to current CourtCaptain data rules.`)) return;
    const deletedAt = new Date().toISOString();
    const { error } = await supabase
      .from(entity.table)
      .update({ deleted_at: deletedAt, delete_reason: 'Deleted from Phase 1 UI' })
      .eq('id', row.id);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    if (entity.table === 'players') {
      const { error: rosterError } = await supabase
        .from('team_rosters')
        .update({
          is_active: false,
          deleted_at: deletedAt,
          delete_reason: 'Player deleted from Phase 1 UI',
        })
        .eq('player_id', row.id)
        .eq('is_active', true)
        .is('deleted_at', null);

      if (rosterError) {
        setSnack({ severity: 'error', message: `Player deleted, but active roster cleanup failed: ${rosterError.message}` });
        await loadRows();
        await refreshLookups();
        return;
      }
    }

    setSnack({ severity: 'success', message: 'Record soft deleted.' });
    await loadRows();
    await refreshLookups();
  };

  const setTennisRecordSyncing = (playerId: string, syncing: boolean) => {
    setSyncingTennisRecordPlayerIds((current) => {
      const next = new Set(current);
      if (syncing) next.add(playerId);
      else next.delete(playerId);
      return next;
    });
  };

  const syncPlayerTennisRecord = async (row: RowRecord) => {
    if (readOnly) return;
    const playerId = String(row.id);
    const tennisRecordUrl = profileUrl(row.tennis_record_profile_url);
    if (!tennisRecordUrl) {
      setSnack({ severity: 'error', message: 'Add a TennisRecord profile URL before syncing this player.' });
      return;
    }
    setTennisRecordSyncing(playerId, true);
    const session = (await supabase.auth.getSession()).data.session;
    const response = await fetch('/api/tennisrecord/player-sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ playerId, url: tennisRecordUrl }),
    });
    const result = await response.json() as { changed?: boolean; newEdr?: number; error?: string };
    setTennisRecordSyncing(playerId, false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to sync TennisRecord profile.' });
      await loadRows();
      return;
    }
    setSnack({ severity: 'success', message: result.changed ? `TennisRecord EDR updated to ${formatEdr(result.newEdr)}.` : 'TennisRecord sync complete. EDR unchanged.' });
    await loadRows();
    await refreshLookups();
  };

  const openPlayerFinancial = async (row: RowRecord) => {
    const playerId = String(row.id);
    setFinancialPlayer(row);
    setFinancialBalances([]);
    setFinancialLedger([]);
    setFinancialLoading(true);
    const [balanceResult, ledgerResult] = await Promise.all([
      supabase.from('player_credit_balances').select('*').eq('player_id', playerId),
      supabase.from('player_financial_ledger_entries').select('*').eq('player_id', playerId).order('created_at', { ascending: false }),
    ]);
    setFinancialLoading(false);
    if (balanceResult.error || ledgerResult.error) {
      setSnack({ severity: 'error', message: balanceResult.error?.message || ledgerResult.error?.message || 'Unable to load player financial ledger.' });
      return;
    }
    setFinancialBalances((balanceResult.data || []) as unknown as PlayerCreditBalance[]);
    setFinancialLedger((ledgerResult.data || []) as unknown as PlayerLedgerEntry[]);
  };

  const gridCellKey = (rowId: string, field: string) => `${rowId}:${field}`;

  const gridValue = (row: RowRecord, field: string) => {
    const rowId = String(row.id);
    const value = gridDrafts[rowId]?.[field] ?? row[field] ?? (field === 'is_active' ? false : '');
    return field === 'ntrp_rating' ? normalizeNtrpValue(value) : value;
  };

  const isGridCellChanged = (row: RowRecord, field: string): boolean => {
    const rowId = String(row.id);
    if (!gridDrafts[rowId] || !(field in gridDrafts[rowId])) return false;
    return normalizeGridValueForField(field, gridDrafts[rowId][field]) !== normalizeGridValueForField(field, row[field]);
  };

  const pendingCellCount = useMemo(() => {
    return rows.reduce((count, row) => count + playerGridFields.filter((field) => isGridCellChanged(row, field)).length, 0);
  }, [gridDrafts, rows]);

  const pendingRowCount = useMemo(() => {
    return rows.filter((row) => playerGridFields.some((field) => isGridCellChanged(row, field))).length;
  }, [gridDrafts, rows]);

  const updateGridCell = (row: RowRecord, field: string, value: string | boolean) => {
    const rowId = String(row.id);
    setGridDrafts((current) => {
      const nextRow = { ...(current[rowId] || {}) };
      if (normalizeGridValueForField(field, value) === normalizeGridValueForField(field, row[field])) {
        delete nextRow[field];
      } else {
        nextRow[field] = value;
      }
      const next = { ...current };
      if (Object.keys(nextRow).length === 0) delete next[rowId];
      else next[rowId] = nextRow;
      return next;
    });
    setGridErrors((current) => {
      const next = { ...current };
      delete next[gridCellKey(rowId, field)];
      return next;
    });
  };

  const enterGridEditMode = () => {
    if (readOnly) return;
    setGridEditMode(true);
    setGridDrafts({});
    setGridErrors({});
  };

  const cancelGridChanges = () => {
    if (pendingCellCount > 0 && !window.confirm('You have unsaved player edits. Discard changes?')) return;
    setGridEditMode(false);
    setGridDrafts({});
    setGridErrors({});
  };

  const validateGridEdits = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    rows.forEach((row) => {
      const rowId = String(row.id);
      if (!gridDrafts[rowId]) return;
      const firstName = gridValue(row, 'first_name');
      const lastName = gridValue(row, 'last_name');
      const sex = gridValue(row, 'sex');
      if (!String(firstName || '').trim()) errors[gridCellKey(rowId, 'first_name')] = 'First Name is required.';
      if (!String(lastName || '').trim()) errors[gridCellKey(rowId, 'last_name')] = 'Last Name is required.';
      if (!String(sex || '').trim()) errors[gridCellKey(rowId, 'sex')] = 'Gender is required.';
      const email = gridValue(row, 'email');
      if (!isValidEmail(email)) errors[gridCellKey(rowId, 'email')] = 'Email must be valid.';
      ['usta_profile_url', 'utr_profile_url', 'tennis_record_profile_url'].forEach((field) => {
        if (!isValidUrl(gridValue(row, field))) errors[gridCellKey(rowId, field)] = 'URL must start with http:// or https://.';
      });
      ['utr_singles_rating', 'utr_doubles_rating', 'estimated_dynamic_rating'].forEach((field) => {
        if (!isOptionalNumber(gridValue(row, field))) errors[gridCellKey(rowId, field)] = 'Value must be numeric.';
      });
      const edr = gridValue(row, 'estimated_dynamic_rating');
      if (edr !== '' && edr !== null && edr !== undefined && (Number(edr) < 1 || Number(edr) > 7)) {
        errors[gridCellKey(rowId, 'estimated_dynamic_rating')] = 'EDR must be between 1.00 and 7.00.';
      }
      const ntrp = gridValue(row, 'ntrp_rating');
      if (ntrp !== '' && ntrp !== null && ntrp !== undefined && !ntrpOptions.includes(String(ntrp))) {
        errors[gridCellKey(rowId, 'ntrp_rating')] = 'Choose a supported NTRP value.';
      }
    });
    const emailOwners = new Map<string, string[]>();
    rows.forEach((row) => {
      const email = normalizeEmailValue(gridValue(row, 'email'));
      if (!email) return;
      const rowId = String(row.id);
      emailOwners.set(email, [...(emailOwners.get(email) || []), rowId]);
    });
    emailOwners.forEach((rowIds) => {
      if (rowIds.length < 2) return;
      rowIds.forEach((rowId) => {
        errors[gridCellKey(rowId, 'email')] = 'Another player already uses this email.';
      });
    });
    return errors;
  };

  const saveGridChanges = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (pendingCellCount === 0) {
      setSnack({ severity: 'success', message: 'No player updates to save.' });
      return;
    }
    const errors = validateGridEdits();
    setGridErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSnack({ severity: 'error', message: 'Fix highlighted player grid cells before saving.' });
      return;
    }

    const changedRows = rows.filter((row) => playerGridFields.some((field) => isGridCellChanged(row, field)));
    const emailChangedRows = changedRows.filter((row) => isGridCellChanged(row, 'email'));
    if (emailChangedRows.length > 0) {
      const clearResults = await Promise.all(emailChangedRows.map((row) => supabase.from('players').update({ email: null }).eq('id', row.id)));
      const clearError = clearResults.find((result) => result.error)?.error;
      if (clearError) {
        setSnack({ severity: 'error', message: clearError.message });
        return;
      }
    }
    const updates = changedRows.map((row) => {
      const payload = playerGridFields.reduce<RowRecord>((acc, field) => {
        if (isGridCellChanged(row, field)) acc[field] = gridPayloadValue(field, gridValue(row, field));
        return acc;
      }, {});
      if (isGridCellChanged(row, 'estimated_dynamic_rating')) {
        payload.estimated_dynamic_rating_updated_at = payload.estimated_dynamic_rating === null ? null : new Date().toISOString();
      }
      return supabase.from('players').update(payload).eq('id', row.id);
    });
    const results = await Promise.all(updates);
    const error = results.find((result) => result.error)?.error;
    if (error) {
      if (emailChangedRows.length > 0) {
        await Promise.all(emailChangedRows.map((row) => supabase.from('players').update({ email: row.email ?? null }).eq('id', row.id)));
        await loadRows();
        await refreshLookups();
      }
      setSnack({ severity: 'error', message: friendlySaveError(error.message) });
      return;
    }
    setGridDrafts({});
    setGridErrors({});
    setGridEditMode(false);
    setSnack({ severity: 'success', message: `${pendingCellCount} player update${pendingCellCount === 1 ? '' : 's'} saved.` });
    await loadRows();
    await refreshLookups();
  };

  useEffect(() => {
    if (!gridEditMode || pendingCellCount === 0) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gridEditMode, pendingCellCount]);

  const cellText = (row: RowRecord, columnName: string): string => {
    const column = entity.columns.find((item) => item.name === columnName);
    if (entity.key === 'players' && columnName === 'full_name') {
      return formatPlayerName({ first_name: String(row.first_name || ''), last_name: String(row.last_name || '') }, userPreferences);
    }
    if (entity.key === 'players' && columnName === 'player_profiles') {
      return [
        profileUrl(row.utr_profile_url) ? 'UTR' : '',
        profileUrl(row.usta_profile_url) ? 'USTA' : '',
        profileUrl(row.tennis_record_profile_url) ? 'TennisRecord' : '',
      ].filter(Boolean).join(' | ') || '-';
    }
    if (column?.format) return column.format(row[columnName], row, lookups);
    return lookupLabel(lookups, column?.lookupKey, row[columnName]);
  };

  const renderCell = (row: RowRecord, columnName: string) => {
    if (entity.key !== 'players' || columnName !== 'player_profiles') return cellText(row, columnName);

    const links = [
      { label: 'UTR', url: profileUrl(row.utr_profile_url) },
      { label: 'USTA', url: profileUrl(row.usta_profile_url) },
      { label: 'TennisRecord', url: profileUrl(row.tennis_record_profile_url) },
    ].filter((link): link is { label: string; url: string } => Boolean(link.url));

    if (links.length === 0) return '—';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
        {links.map((link) => (
          <Link key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" underline="hover">
            {link.label}
          </Link>
        ))}
      </Box>
    );
  };

  const compareRows = (a: RowRecord, b: RowRecord, field: string, direction: SortDirection): number => {
    const multiplier = direction === 'asc' ? 1 : -1;
    if (entity.key === 'players' && field === 'full_name') {
      return comparePlayerNames(
        { first_name: String(a.first_name || ''), last_name: String(a.last_name || ''), sex: String(a.sex || '') },
        { first_name: String(b.first_name || ''), last_name: String(b.last_name || ''), sex: String(b.sex || '') },
        userPreferences,
      ) * multiplier;
    }
    const aRaw = a[field];
    const bRaw = b[field];
    if (field === 'is_primary_captain') {
      return (Number(Boolean(aRaw)) - Number(Boolean(bRaw))) * multiplier;
    }
    if (typeof aRaw === 'number' || typeof bRaw === 'number') {
      return ((Number(aRaw || 0) - Number(bRaw || 0)) || cellText(a, field).localeCompare(cellText(b, field), undefined, { sensitivity: 'base' })) * multiplier;
    }
    const aValue = typeof aRaw === 'boolean' ? (aRaw ? 'Active' : 'Inactive') : cellText(a, field);
    const bValue = typeof bRaw === 'boolean' ? (bRaw ? 'Active' : 'Inactive') : cellText(b, field);
    return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
  };

  const sortByRules = (candidateRows: RowRecord[], sortRules: SortRule[]): RowRecord[] => {
    return [...candidateRows].sort((a, b) => {
      if (entity.key === 'players' && userPreferences.groupPlayersBySex) {
        const sexCompare = String(a.sex || '').localeCompare(String(b.sex || ''), undefined, { sensitivity: 'base' });
        if (sexCompare !== 0) return sexCompare;
      }
      for (const rule of sortRules) {
        const result = compareRows(a, b, rule.field, rule.direction);
        if (result !== 0) return result;
      }
      return 0;
    });
  };

  const playerAssignmentsByPlayerId = useMemo(() => {
    const activeTeamIds = new Set(lookups.teams.map((team) => team.value));
    const next = new Map<string, PlayerRosterAssignment[]>();
    playerRosterAssignments.forEach((assignment) => {
      if (!assignment.player_id || !activeTeamIds.has(assignment.team_id)) return;
      if ((assignment.roster_status || 'active') === 'inactive') return;
      const playerId = String(assignment.player_id);
      const current = next.get(playerId) || [];
      if (current.some((item) => item.team_id === assignment.team_id)) return;
      next.set(playerId, [...current, assignment]);
    });
    return next;
  }, [lookups.teams, playerRosterAssignments]);

  const selectedPlayer = useMemo(
    () => rows.find((row) => String(row.id) === playerDrawerId) || null,
    [playerDrawerId, rows],
  );

  useEffect(() => {
    if (isPlayers && playerDrawerId && rows.length > 0 && !selectedPlayer) {
      setPlayerDrawerId(null);
    }
  }, [isPlayers, playerDrawerId, rows.length, selectedPlayer]);

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setFacilitySearch(savedViewState.facilitySearch);
    setPlayerSearch(savedViewState.playerSearch);
    setPlayerSexFilter(savedViewState.playerSexFilter);
    setPlayerNtrpFilter(savedViewState.playerNtrpFilter);
    setPlayerActiveFilter(savedViewState.playerActiveFilter);
    setPlayerQuickFilter(savedViewState.playerQuickFilter);
    setPlayerDrawerId(savedViewState.playerDrawerId);
    setPlayerDrawerTab(savedViewState.playerDrawerTab);
    setActiveSort(savedViewState.activeSort);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({
      facilitySearch,
      playerSearch,
      playerSexFilter,
      playerNtrpFilter,
      playerActiveFilter,
      playerQuickFilter,
      playerDrawerId,
      playerDrawerTab,
      activeSort,
    });
  }, [activeSort, facilitySearch, playerActiveFilter, playerDrawerId, playerDrawerTab, playerNtrpFilter, playerQuickFilter, playerSearch, playerSexFilter, setSavedViewState, viewStateKey, viewStateReady]);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: entity.key,
    ready: !loading,
  });

  const selectedActionPlayer = useMemo(
    () => rows.find((row) => String(row.id) === playerActionRowId) || null,
    [playerActionRowId, rows],
  );

  const playerInitials = (row: RowRecord) => `${String(row.first_name || '').trim().slice(0, 1)}${String(row.last_name || '').trim().slice(0, 1)}`.toUpperCase() || 'P';
  const playerFullName = (row: RowRecord) => formatPlayerName({ first_name: String(row.first_name || ''), last_name: String(row.last_name || '') }, userPreferences);
  const playerActiveTeamCount = (row: RowRecord) => playerAssignmentsByPlayerId.get(String(row.id))?.length || 0;
  const genderLabel = (value: unknown) => sexOptions.find((option) => option.value === value)?.label || 'Gender missing';
  const ntrpLabel = (value: unknown) => normalizeNtrpValue(value) || '—';
  const ratingLabel = (value: unknown) => (value === null || value === undefined || value === '' ? '—' : String(value));
  const profileLinked = (value: unknown) => Boolean(profileUrl(value));
  const tennisRecordLinked = (row: RowRecord) => profileLinked(row.tennis_record_profile_url);
  const utrLinked = (row: RowRecord) => profileLinked(row.utr_profile_url);
  const tennisRecordSyncStatus = (row: RowRecord) => String(row.tennis_record_sync_status || (tennisRecordLinked(row) ? 'not synced' : 'not linked')).replaceAll('_', ' ');
  const singlesPreferenceLabel = (value: unknown) => singlesPreferenceOptions.find((option) => option.value === value)?.label || 'If Possible';

  const playerAttentionReasons = (row: RowRecord) => {
    const reasons: string[] = [];
    const missingRatings = !row.ntrp_rating && !row.estimated_dynamic_rating;
    const failedSync = String(row.tennis_record_sync_status || '').toLowerCase() === 'failed';
    if (!row.sex) reasons.push('Missing gender');
    if (missingRatings) reasons.push('Missing NTRP/EDR');
    if (!row.email || !row.mobile_phone) reasons.push('Missing contact info');
    if (!tennisRecordLinked(row)) reasons.push('Missing TennisRecord URL');
    if (failedSync) reasons.push('TennisRecord sync failed');
    return reasons;
  };
  const playerNeedsAttention = (row: RowRecord) => playerAttentionReasons(row).length > 0;

  const playerSummary = useMemo(() => {
    const activeRows = rows.filter((row) => row.is_active !== false);
    return {
      total: rows.length,
      active: activeRows.length,
      men: rows.filter((row) => row.sex === 'male').length,
      women: rows.filter((row) => row.sex === 'female').length,
      tennisLinked: rows.filter(tennisRecordLinked).length,
      needsAttention: rows.filter(playerNeedsAttention).length,
    };
  }, [rows]);

  const openPlayerDrawer = (row: RowRecord, tab: PlayerDrawerTab = 'overview') => {
    setPlayerDrawerId(String(row.id));
    setPlayerDrawerTab(tab);
  };

  const closePlayerActionMenu = () => {
    setPlayerActionAnchor(null);
    setPlayerActionRowId(null);
  };

  const togglePlayerActive = async (row: RowRecord) => {
    if (readOnly) return;
    const nextActive = row.is_active === false;
    const { error } = await supabase.from('players').update({ is_active: nextActive }).eq('id', row.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: nextActive ? 'Player reactivated.' : 'Player deactivated.' });
    await loadRows();
    await refreshLookups();
  };

  const menuAction = (action: (row: RowRecord) => void | Promise<void>) => {
    const row = selectedActionPlayer;
    closePlayerActionMenu();
    if (row) void action(row);
  };

  const renderedRows = useMemo(() => {
    const normalizedSearch = facilitySearch.trim().toLowerCase();
    const normalizedPlayerSearch = playerSearch.trim().toLowerCase();
    let filteredRows = isFacilities && normalizedSearch
      ? rows.filter((row) => facilitySearchColumns.some((field) => String(row[field] || '').toLowerCase().includes(normalizedSearch)))
      : rows;
    if (isPlayers) {
      filteredRows = filteredRows
        .filter((row) => {
          if (!normalizedPlayerSearch) return true;
          const fullName = `${row.first_name || ''} ${row.last_name || ''}`.toLowerCase();
          return fullName.includes(normalizedPlayerSearch) || String(row.email || '').toLowerCase().includes(normalizedPlayerSearch) || String(row.mobile_phone || '').toLowerCase().includes(normalizedPlayerSearch);
        })
        .filter((row) => !playerSexFilter || row.sex === playerSexFilter)
        .filter((row) => !playerNtrpFilter || normalizeNtrpValue(row.ntrp_rating) === playerNtrpFilter)
        .filter((row) => playerActiveFilter === 'all' || (playerActiveFilter === 'active' ? row.is_active !== false : row.is_active === false))
        .filter((row) => {
          if (playerQuickFilter === 'all') return true;
          if (playerQuickFilter === 'needs_attention') return playerNeedsAttention(row);
          if (playerQuickFilter === 'missing_ratings') return !row.ntrp_rating && !row.estimated_dynamic_rating;
          if (playerQuickFilter === 'not_linked') return !tennisRecordLinked(row);
          if (playerQuickFilter === 'linked') return tennisRecordLinked(row);
          if (playerQuickFilter === 'not_assigned') return playerActiveTeamCount(row) === 0;
          return true;
        });
    }

    const sortRules = activeSort.field ? [{ field: activeSort.field, direction: activeSort.direction }] : entity.defaultSort || [];
    return sortRules.length > 0 ? sortByRules(filteredRows, sortRules) : filteredRows;
  }, [activeSort, entity.defaultSort, facilitySearch, isFacilities, isPlayers, playerActiveFilter, playerNtrpFilter, playerQuickFilter, playerSearch, playerSexFilter, rows, userPreferences, lookups, playerAssignmentsByPlayerId]);

  const toggleSort = (field: string) => {
    setActiveSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderGridInput = (row: RowRecord, field: (typeof playerGridFields)[number]) => {
    const rowId = String(row.id);
    const value = gridValue(row, field);
    const changed = isGridCellChanged(row, field);
    const error = gridErrors[gridCellKey(rowId, field)];
    const sx = {
      minWidth: field === 'notes' ? 260 : field.includes('url') || field === 'email' ? 220 : 140,
      bgcolor: changed ? 'warning.light' : undefined,
      '& .MuiInputBase-input': { py: 0.75, fontSize: 13 },
    };
    if (field === 'sex') {
      return (
        <FormControl fullWidth error={Boolean(error)} size="small" sx={sx}>
          <Select value={String(value || '')} onChange={(event) => updateGridCell(row, field, event.target.value)}>
            {sexOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'ntrp_rating') {
      return (
        <FormControl fullWidth error={Boolean(error)} size="small" sx={sx}>
          <Select value={String(value ?? '')} onChange={(event) => updateGridCell(row, field, event.target.value)}>
            {ntrpOptions.map((option) => <MenuItem key={option || 'blank'} value={option}>{option || 'None'}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'is_active') {
      return (
        <Checkbox
          checked={Boolean(value)}
          onChange={(event) => updateGridCell(row, field, event.target.checked)}
          sx={{ bgcolor: changed ? 'warning.light' : undefined, borderRadius: 1 }}
        />
      );
    }
    return (
      <TextField
        size="small"
        value={value ?? ''}
        error={Boolean(error)}
        helperText={error || ''}
        type={field === 'birthday' ? 'date' : ['utr_singles_rating', 'utr_doubles_rating', 'estimated_dynamic_rating'].includes(field) ? 'number' : field === 'email' ? 'email' : 'text'}
        multiline={field === 'notes'}
        minRows={field === 'notes' ? 2 : undefined}
        InputLabelProps={field === 'birthday' ? { shrink: true } : undefined}
        inputProps={field === 'estimated_dynamic_rating' ? { step: 0.01, min: 1, max: 7 } : ['utr_singles_rating', 'utr_doubles_rating'].includes(field) ? { step: 0.01, min: 0 } : undefined}
        sx={sx}
        onChange={(event) => updateGridCell(row, field, event.target.value)}
      />
    );
  };

  const collectionHasActiveFilters = Boolean(
    (isFacilities && facilitySearch.trim())
    || (isPlayers && (
      playerSearch.trim()
      || playerSexFilter
      || playerNtrpFilter
      || playerActiveFilter !== 'active'
      || playerQuickFilter !== 'all'
    )),
  );
  const emptyStateTitle = rows.length === 0
    ? `No ${title.toLowerCase()} have been added yet.`
    : `No ${title.toLowerCase()} match these filters.`;
  const emptyStateMessage = rows.length === 0
    ? undefined
    : 'Adjust or clear filters to see more records.';
  const fieldMessageNode = (fieldName: string): ReactNode => {
    const error = formErrors[fieldName];
    const warnings = formWarnings[fieldName] || [];
    if (!error && warnings.length === 0) return undefined;
    return (
      <Stack spacing={0.25}>
        {error && <span>{error}</span>}
        {warnings.map((warning) => (
          <Typography key={warning} component="span" variant="caption" color="warning.main">
            {warning}
          </Typography>
        ))}
      </Stack>
    );
  };
  const fieldHasWarning = (fieldName: string) => Boolean(formWarnings[fieldName]?.length);
  const safeUrl = (fieldName: string) => parseSafeUrl(form[fieldName])?.toString() || '';

  const renderSpecialTextField = (
    fieldName: string,
    label: string,
    options: {
      required?: boolean;
      type?: string;
      multiline?: boolean;
      minRows?: number;
      inputProps?: Record<string, unknown>;
      gridColumn?: string;
    } = {},
  ) => (
    <TextField
      key={fieldName}
      label={label}
      value={form[fieldName] ?? ''}
      required={options.required}
      type={options.type || 'text'}
      error={Boolean(formErrors[fieldName])}
      color={fieldHasWarning(fieldName) ? 'warning' : undefined}
      helperText={fieldMessageNode(fieldName)}
      FormHelperTextProps={{ component: 'div' }}
      fullWidth
      multiline={options.multiline}
      minRows={options.minRows}
      InputLabelProps={options.type === 'date' ? { shrink: true } : undefined}
      inputProps={options.inputProps}
      sx={options.gridColumn ? { gridColumn: options.gridColumn } : undefined}
      onBlur={() => normalizeFieldOnBlur(fieldName)}
      onChange={(event) => updateFormField(fieldName, event.target.value)}
    />
  );

  const renderSpecialSelectField = (fieldName: string, label: string, options: { value: string; label: string }[], required = false) => (
    <FormControl key={fieldName} fullWidth required={required} error={Boolean(formErrors[fieldName])} color={fieldHasWarning(fieldName) ? 'warning' : undefined}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={typeof form[fieldName] === 'string' || typeof form[fieldName] === 'number' ? String(form[fieldName] ?? '') : ''}
        onBlur={() => normalizeFieldOnBlur(fieldName)}
        onChange={(event) => updateFormField(fieldName, event.target.value)}
      >
        {!required && <MenuItem value="">None</MenuItem>}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>
      {fieldMessageNode(fieldName) && <FormHelperText component="div">{fieldMessageNode(fieldName)}</FormHelperText>}
    </FormControl>
  );

  const renderSpecialCheckbox = (fieldName: string, label: string) => (
    <FormControl key={fieldName} error={Boolean(formErrors[fieldName])} sx={{ gridColumn: '1 / -1' }}>
      <FormControlLabel
        control={<Checkbox checked={Boolean(form[fieldName])} onChange={(event) => updateFormField(fieldName, event.target.checked)} />}
        label={label}
      />
      {fieldMessageNode(fieldName) && <FormHelperText component="div">{fieldMessageNode(fieldName)}</FormHelperText>}
    </FormControl>
  );

  const renderSpecialUrlField = (fieldName: string, label: string, openLabel = 'Open Link') => {
    const href = safeUrl(fieldName);
    return (
      <Stack key={fieldName} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          {renderSpecialTextField(fieldName, label, { type: 'url' })}
        </Box>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          disabled={!href || Boolean(formErrors[fieldName])}
          onClick={() => {
            if (href) window.open(href, '_blank', 'noopener,noreferrer');
          }}
          sx={{ mt: { sm: 1 } }}
        >
          {openLabel}
        </Button>
      </Stack>
    );
  };

  const renderFormSection = (sectionTitle: string, children: ReactNode) => (
    <Paper variant="outlined" sx={{ p: 2, gridColumn: '1 / -1' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>{sectionTitle}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {children}
      </Box>
    </Paper>
  );

  const renderValidationSummary = () => {
    const errorMessages = Object.values(formErrors);
    if (errorMessages.length === 0 && globalWarnings.length === 0) return null;
    return (
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {errorMessages.length > 0 && (
          <Alert severity="error">
            <AlertTitle>Fix These Fields</AlertTitle>
            {errorMessages.slice(0, 4).map((message) => (
              <Typography key={message} variant="body2">{message}</Typography>
            ))}
            {errorMessages.length > 4 && <Typography variant="body2">And {errorMessages.length - 4} more.</Typography>}
          </Alert>
        )}
        {globalWarnings.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>Review Before Saving</AlertTitle>
            <Stack spacing={0.75}>
              {globalWarnings.map((warning, index) => (
                <Typography key={`${warning.field || 'warning'}:${index}`} variant="body2">
                  {warning.message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  };

  const renderPlayerForm = () => (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {renderValidationSummary()}
      <DraftSavedIndicator savedAt={formDraftPersistence.draftSavedAt} />
      {renderFormSection('Identity', (
        <>
          {renderSpecialTextField('first_name', 'First Name', { required: true })}
          {renderSpecialTextField('last_name', 'Last Name', { required: true })}
          {renderSpecialSelectField('sex', 'Gender', sexOptions, true)}
          {renderSpecialSelectField('singles_preference', 'Singles Preference', singlesPreferenceOptions)}
          {renderSpecialSelectField('back_to_back_match_day_tolerance', 'Back-to-Back Match Day Tolerance', backToBackMatchDayToleranceOptions)}
          {renderSpecialSelectField('same_day_match_tolerance', 'Same-Day Match Tolerance', backToBackMatchDayToleranceOptions)}
          {renderSpecialTextField('minimum_recovery_hours', 'Minimum Recovery Hours', { type: 'number', inputProps: { min: 0, max: 72, step: 1 } })}
          {renderSpecialTextField('birthday', 'Birthday', { type: 'date' })}
          {renderSpecialTextField('groupme_display_name', 'GroupMe Display Name')}
          {renderSpecialCheckbox('is_active', 'Active')}
        </>
      ))}
      {renderFormSection('Contact', (
        <>
          {renderSpecialTextField('email', 'Email', { type: 'email' })}
          {renderSpecialTextField('mobile_phone', 'Mobile Phone', { type: 'tel', inputProps: { inputMode: 'tel' } })}
        </>
      ))}
      {renderFormSection('Address', (
        <>
          {renderSpecialTextField('address_line1', 'Address Line 1')}
          {renderSpecialTextField('address_line2', 'Address Line 2')}
          {renderSpecialTextField('city', 'City')}
          {renderSpecialSelectField('state', 'State', usStateOptions)}
          {renderSpecialTextField('zip_code', 'ZIP Code', { inputProps: { inputMode: 'numeric' } })}
        </>
      ))}
      {renderFormSection('Ratings', (
        <>
          {renderSpecialTextField('utr_rating', 'Overall UTR', { type: 'number', inputProps: { min: 0, max: 16.5, step: 0.01 } })}
          {renderSpecialTextField('utr_singles_rating', 'Singles UTR', { type: 'number', inputProps: { min: 0, max: 16.5, step: 0.01 } })}
          {renderSpecialTextField('utr_doubles_rating', 'Doubles UTR', { type: 'number', inputProps: { min: 0, max: 16.5, step: 0.01 } })}
          {renderSpecialSelectField('ntrp_rating', 'NTRP Rating', ntrpOptions.filter(Boolean).map((option) => ({ value: option, label: option })))}
          {renderSpecialTextField('estimated_dynamic_rating', 'Estimated Dynamic Rating (EDR)', { type: 'number', inputProps: { min: 1, max: 7, step: 0.01 } })}
        </>
      ))}
      {renderFormSection('Profile Links', (
        <>
          {renderSpecialUrlField('tennis_record_profile_url', 'TennisRecord Profile URL', 'Open TennisRecord')}
          {renderSpecialUrlField('utr_profile_url', 'UTR Profile URL', 'Open UTR')}
          {renderSpecialUrlField('usta_profile_url', 'USTA Profile URL', 'Open USTA')}
        </>
      ))}
      {renderFormSection('Notes', renderSpecialTextField('notes', 'Notes', { multiline: true, minRows: 4, gridColumn: '1 / -1' }))}
    </Stack>
  );

  const renderFacilityForm = () => (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {renderValidationSummary()}
      <DraftSavedIndicator savedAt={formDraftPersistence.draftSavedAt} />
      {renderFormSection('Facility', (
        <>
          {renderSpecialTextField('facility_name', 'Facility Name', { required: true })}
          {renderSpecialCheckbox('is_active', 'Active')}
        </>
      ))}
      {renderFormSection('Address', (
        <>
          {renderSpecialTextField('address_line1', 'Address Line 1')}
          {renderSpecialTextField('address_line2', 'Address Line 2')}
          {renderSpecialTextField('city', 'City')}
          {renderSpecialSelectField('state', 'State', usStateOptions)}
          {renderSpecialTextField('zip_code', 'ZIP Code', { inputProps: { inputMode: 'numeric' } })}
        </>
      ))}
      {renderFormSection('Links', (
        <>
          <Stack spacing={1}>
            {renderSpecialUrlField('google_maps_url', 'Google Maps URL', 'Open Map')}
            <Button
              variant="outlined"
              onClick={() => updateFormField('google_maps_url', googleMapsSearchUrl(form))}
              disabled={!String(form.facility_name || form.address_line1 || form.city || '').trim()}
            >
              Generate Map Link
            </Button>
          </Stack>
          {renderSpecialUrlField('warmup_booking_url', 'Facility Website', 'Open Website')}
        </>
      ))}
      {renderFormSection('Notes', renderSpecialTextField('notes', 'Notes', { multiline: true, minRows: 4, gridColumn: '1 / -1' }))}
    </Stack>
  );

  const renderGenericForm = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
      {entity.fields.map((field) => {
        const value = form[field.name];
        if (field.type === 'boolean') {
          return (
            <FormControlLabel
              key={field.name}
              control={
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(event) => setForm((current) => ({ ...current, [field.name]: event.target.checked }))}
                />
              }
              label={field.label}
            />
          );
        }
        if (field.type === 'select') {
          const options = field.options || (field.optionsKey ? lookups[field.optionsKey] : []);
          return (
            <FormControl key={field.name} fullWidth required={field.required}>
              <InputLabel>{field.label}</InputLabel>
              <Select
                label={field.label}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => setForm((current) => ({ ...current, [field.name]: event.target.value }))}
              >
                {!field.required && <MenuItem value="">None</MenuItem>}
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return (
          <TextField
            key={field.name}
            label={field.label}
            value={value ?? ''}
            required={field.required}
            type={field.type === 'number' || field.type === 'date' || field.type === 'url' ? field.type : 'text'}
            inputProps={
              field.name === 'estimated_dynamic_rating'
                ? { min: 1, max: 7, step: 0.01 }
                : field.name === 'default_target_match_count_per_player' || field.name === 'default_warmup_minutes'
                  ? { min: 0, step: 1 }
                  : field.maxLength
                    ? { maxLength: field.maxLength }
                    : undefined
            }
            multiline={field.type === 'textarea'}
            minRows={field.type === 'textarea' ? 3 : undefined}
            helperText={field.helperText}
            fullWidth
            InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
            sx={field.type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}
            onChange={(event) => setForm((current) => ({ ...current, [field.name]: event.target.value }))}
          />
        );
      })}
    </Box>
  );

  const renderPlayerSummaryCard = (label: string, value: ReactNode, onClick?: () => void) => (
    <Paper
      key={label}
      variant="outlined"
      component={onClick ? 'button' : 'div'}
      onClick={onClick}
      aria-label={`${label}: ${value}`}
      sx={{
        p: 1.5,
        minHeight: 78,
        textAlign: 'left',
        bgcolor: 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 2,
        borderColor: onClick ? 'success.light' : 'divider',
        '&:focus-visible': { outline: '3px solid', outlineColor: 'success.light', outlineOffset: 2 },
      }}
    >
      <Typography variant="h5" color="success.dark" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );

  const renderIntegrationRow = (row: RowRecord, provider: 'tennisrecord' | 'utr', inDrawer = false) => {
    const isTennisRecord = provider === 'tennisrecord';
    const linked = isTennisRecord ? tennisRecordLinked(row) : utrLinked(row);
    const status = isTennisRecord
      ? (linked ? tennisRecordSyncStatus(row) : 'not linked')
      : (linked ? 'linked' : (row.utr_singles_rating || row.utr_doubles_rating || row.utr_rating ? 'manual ratings' : 'not linked'));
    const color = linked || status === 'manual ratings' ? 'success.main' : 'warning.main';
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: inDrawer ? 'auto 1fr auto' : '1fr auto', gap: 1, alignItems: 'center' }}>
        {inDrawer && (
          <Avatar sx={{ width: 32, height: 32, bgcolor: isTennisRecord ? 'success.dark' : 'primary.main', fontSize: 12 }}>
            {isTennisRecord ? 'TR' : 'UTR'}
          </Avatar>
        )}
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {linked || status === 'manual ratings' ? <CheckCircleIcon sx={{ fontSize: 16, color }} /> : <WarningAmberIcon sx={{ fontSize: 16, color }} />}
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{isTennisRecord ? 'TennisRecord' : 'UTR'}</Typography>
          </Stack>
          <Typography variant="caption" color={linked || status === 'manual ratings' ? 'success.dark' : 'text.secondary'}>
            {status}
          </Typography>
          {inDrawer && isTennisRecord && row.tennis_record_last_successful_sync_at && (
            <Typography variant="caption" color="text.secondary" display="block">
              Last synced {new Date(String(row.tennis_record_last_successful_sync_at)).toLocaleString()}
            </Typography>
          )}
          {inDrawer && isTennisRecord && row.tennis_record_last_sync_error && (
            <Typography variant="caption" color="error" display="block">
              {String(row.tennis_record_last_sync_error)}
            </Typography>
          )}
        </Box>
        {inDrawer ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={!profileUrl(isTennisRecord ? row.tennis_record_profile_url : row.utr_profile_url)}
              onClick={() => {
                const url = profileUrl(isTennisRecord ? row.tennis_record_profile_url : row.utr_profile_url);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              Open Profile
            </Button>
            {isTennisRecord && (
              <Button
                size="small"
                variant="contained"
                disabled={readOnly || syncingTennisRecordPlayerIds.has(String(row.id)) || !tennisRecordLinked(row)}
                onClick={() => void syncPlayerTennisRecord(row)}
              >
                Sync Now
              </Button>
            )}
          </Stack>
        ) : (
          <Button size="small" onClick={(event) => { event.stopPropagation(); openPlayerDrawer(row, 'overview'); }}>
            ›
          </Button>
        )}
      </Box>
    );
  };

  const renderPlayerDrawerSection = (titleText: string, children: ReactNode, onEdit?: () => void) => (
    <Box sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{titleText}</Typography>
        {onEdit && !readOnly && <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>}
      </Stack>
      {children}
    </Box>
  );

  const renderPlayerDrawer = () => {
    if (!selectedPlayer) return null;
    const assignments = playerAssignmentsByPlayerId.get(String(selectedPlayer.id)) || [];
    return (
      <Drawer
        anchor="right"
        open={Boolean(selectedPlayer)}
        onClose={() => setPlayerDrawerId(null)}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100%', zIndex: (theme) => theme.zIndex.drawer + 2 } }}
      >
        <Stack sx={{ height: '100%' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'success.dark', width: 54, height: 54, fontWeight: 800 }}>{playerInitials(selectedPlayer)}</Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{playerFullName(selectedPlayer)}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={selectedPlayer.is_active === false ? 'default' : 'success'} label={selectedPlayer.is_active === false ? 'Inactive' : 'Active'} />
                    <Typography variant="caption" color="text.secondary">
                      {genderLabel(selectedPlayer.sex)} • {playerActiveTeamCount(selectedPlayer)} active team{playerActiveTeamCount(selectedPlayer) === 1 ? '' : 's'}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
              <IconButton aria-label="Close player details" onClick={() => setPlayerDrawerId(null)}><CloseIcon /></IconButton>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {(['overview', 'teams', 'history'] as PlayerDrawerTab[]).map((tab) => (
                <Button
                  key={tab}
                  size="small"
                  variant={playerDrawerTab === tab ? 'contained' : 'text'}
                  onClick={() => setPlayerDrawerTab(tab)}
                >
                  {tab === 'overview' ? 'Overview' : tab === 'teams' ? `Teams (${assignments.length})` : 'History'}
                </Button>
              ))}
            </Stack>
          </Box>
          <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            {playerDrawerTab === 'overview' && (
              <>
                {playerAttentionReasons(selectedPlayer).length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>Needs Attention</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {playerAttentionReasons(selectedPlayer).map((reason) => (
                        <Chip key={reason} size="small" color="warning" variant="outlined" label={reason} />
                      ))}
                    </Stack>
                  </Alert>
                )}
                {renderPlayerDrawerSection('Contact', (
                  <Stack spacing={0.75}>
                    <Typography variant="body2"><strong>Email</strong> {selectedPlayer.email || '—'}</Typography>
                    <Typography variant="body2"><strong>Phone</strong> {selectedPlayer.mobile_phone || '—'}</Typography>
                    <Typography variant="body2"><strong>GroupMe Name</strong> {selectedPlayer.groupme_display_name || '—'}</Typography>
                  </Stack>
                ), () => openEdit(selectedPlayer))}
                {renderPlayerDrawerSection('Ratings', (
                  <Stack spacing={0.75}>
                    <Typography variant="body2"><strong>NTRP</strong> {ntrpLabel(selectedPlayer.ntrp_rating)}</Typography>
                    <Typography variant="body2"><strong>EDR</strong> {formatEdr(selectedPlayer.estimated_dynamic_rating)}</Typography>
                    <Typography variant="body2"><strong>UTR Singles</strong> {ratingLabel(selectedPlayer.utr_singles_rating)}</Typography>
                    <Typography variant="body2"><strong>UTR Doubles</strong> {ratingLabel(selectedPlayer.utr_doubles_rating)}</Typography>
                  </Stack>
                ), () => openEdit(selectedPlayer))}
                {renderPlayerDrawerSection('Singles Preference', (
                  <Chip size="small" color={selectedPlayer.singles_preference === 'yes' ? 'success' : selectedPlayer.singles_preference === 'no' ? 'default' : 'warning'} label={singlesPreferenceLabel(selectedPlayer.singles_preference)} />
                ), () => openEdit(selectedPlayer))}
                {renderPlayerDrawerSection('Integrations', (
                  <Stack spacing={1.5}>
                    {renderIntegrationRow(selectedPlayer, 'tennisrecord', true)}
                    <Divider />
                    {renderIntegrationRow(selectedPlayer, 'utr', true)}
                  </Stack>
                ), () => openEdit(selectedPlayer))}
                <PlayerWelcomeEmailPanel playerId={String(selectedPlayer.id)} hasEmail={Boolean(String(selectedPlayer.email || '').trim())} />
              </>
            )}
            {playerDrawerTab === 'teams' && (
              <Stack spacing={1}>
                {assignments.map((assignment) => {
                  const team = lookups.teams.find((item) => item.value === assignment.team_id);
                  const season = lookups.seasons.find((item) => item.value === team?.season_id);
                  return (
                    <Paper key={assignment.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{team?.label || assignment.team_id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[season?.label, assignment.roster_role?.replaceAll('_', ' '), assignment.roster_status, assignment.player_tier].filter(Boolean).join(' • ') || 'Active roster assignment'}
                      </Typography>
                    </Paper>
                  );
                })}
                {assignments.length === 0 && <Typography variant="body2" color="text.secondary">This player is not assigned to an active team.</Typography>}
              </Stack>
            )}
            {playerDrawerTab === 'history' && (
              <Stack spacing={1.5}>
                <Alert severity="info">Player activity history is shown where CourtCaptain currently captures it. Financial history remains available below.</Alert>
                <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => void openPlayerFinancial(selectedPlayer)}>
                  Open Financial History
                </Button>
              </Stack>
            )}
          </Box>
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Stack direction="row" spacing={1}>
              {!readOnly && <Button fullWidth variant="contained" onClick={() => openEdit(selectedPlayer)}>Edit Player</Button>}
              <Button fullWidth variant="outlined" onClick={() => void openPlayerFinancial(selectedPlayer)}>Financial History</Button>
            </Stack>
          </Box>
        </Stack>
      </Drawer>
    );
  };

  const renderOptimizedPlayersView = () => (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
        {renderPlayerSummaryCard('Total Players', playerSummary.total)}
        {renderPlayerSummaryCard('Active Players', playerSummary.active, () => setPlayerActiveFilter('active'))}
        {renderPlayerSummaryCard('Men / Women', `${playerSummary.men} / ${playerSummary.women}`)}
        {renderPlayerSummaryCard('TennisRecord Linked', playerSummary.tennisLinked, () => setPlayerQuickFilter('linked'))}
        {renderPlayerSummaryCard('Need Attention', playerSummary.needsAttention, () => setPlayerQuickFilter('needs_attention'))}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr repeat(3, 1fr) auto auto' }, gap: 1.5, alignItems: 'center' }}>
            <TextField size="small" label="Search players" value={playerSearch} onChange={(event) => setPlayerSearch(event.target.value)} placeholder="Search by name, email, or phone" />
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select label="Gender" value={playerSexFilter} onChange={(event) => setPlayerSexFilter(event.target.value)}>
                <MenuItem value="">All</MenuItem>
                {sexOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>NTRP</InputLabel>
              <Select label="NTRP" value={playerNtrpFilter} onChange={(event) => setPlayerNtrpFilter(event.target.value)}>
                <MenuItem value="">All</MenuItem>
                {ntrpOptions.filter(Boolean).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={playerActiveFilter} onChange={(event) => setPlayerActiveFilter(event.target.value)}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={(event) => setPlayerMoreFilterAnchor(event.currentTarget)}>More Filters</Button>
            <Button variant="outlined" onClick={(event) => setPlayerViewAnchor(event.currentTarget)}>View</Button>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[
              ['all', 'All Players'],
              ['needs_attention', 'Needs Attention'],
              ['missing_ratings', 'Missing Ratings'],
              ['not_linked', 'Not Linked to TennisRecord'],
              ['not_assigned', 'Not Assigned to a Team'],
            ].map(([value, label]) => (
              <Chip
                key={value}
                label={label}
                color={playerQuickFilter === value ? 'success' : 'default'}
                variant={playerQuickFilter === value ? 'filled' : 'outlined'}
                onClick={() => setPlayerQuickFilter(value as PlayerQuickFilter)}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Player</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Ratings</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Singles Preference</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Integrations</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderedRows.map((row) => (
                <TableRow key={String(row.id)} hover sx={{ cursor: 'pointer' }} onClick={() => openPlayerDrawer(row)}>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ bgcolor: 'success.dark', width: 38, height: 38, fontSize: 14, fontWeight: 800 }}>{playerInitials(row)}</Avatar>
                      <Box>
                        <Typography variant="body2" color="success.dark" sx={{ fontWeight: 800 }}>{playerFullName(row)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {genderLabel(row.sex)} • {playerActiveTeamCount(row)} active team{playerActiveTeamCount(row) === 1 ? '' : 's'}
                        </Typography>
                        {playerAttentionReasons(row).length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {playerAttentionReasons(row).map((reason) => (
                              <Chip key={reason} size="small" color="warning" variant="outlined" label={reason} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">NTRP {ntrpLabel(row.ntrp_rating)} • EDR {formatEdr(row.estimated_dynamic_rating)}</Typography>
                    <Typography variant="caption" color="text.secondary">UTR S {ratingLabel(row.utr_singles_rating)} • UTR D {ratingLabel(row.utr_doubles_rating)}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" label={singlesPreferenceLabel(row.singles_preference)} /></TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {renderIntegrationRow(row, 'tennisrecord')}
                      {renderIntegrationRow(row, 'utr')}
                    </Stack>
                  </TableCell>
                  <TableCell><Chip size="small" color={row.is_active === false ? 'default' : 'success'} label={row.is_active === false ? 'Inactive' : 'Active'} /></TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={`Actions for ${playerFullName(row)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPlayerActionAnchor(event.currentTarget);
                        setPlayerActionRowId(String(row.id));
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {renderedRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No players match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu anchorEl={playerViewAnchor} open={Boolean(playerViewAnchor)} onClose={() => setPlayerViewAnchor(null)}>
        <MenuItem onClick={() => onUserPreferencesChange?.({ playerNameDisplayFormat: userPreferences.playerNameDisplayFormat === 'first_last' ? 'last_first' : 'first_last' })}>
          Name Format: {userPreferences.playerNameDisplayFormat === 'first_last' ? 'First Last' : 'Last, First'}
        </MenuItem>
        <MenuItem onClick={() => onUserPreferencesChange?.({ playerSortOrder: userPreferences.playerSortOrder === 'first_name' ? 'last_name' : 'first_name' })}>
          Sorting: {userPreferences.playerSortOrder === 'first_name' ? 'First Name' : 'Last Name'}
        </MenuItem>
        <MenuItem onClick={() => onUserPreferencesChange?.({ groupPlayersBySex: !userPreferences.groupPlayersBySex })}>
          Group by Gender: {userPreferences.groupPlayersBySex ? 'On' : 'Off'}
        </MenuItem>
      </Menu>
      <Menu anchorEl={playerMoreFilterAnchor} open={Boolean(playerMoreFilterAnchor)} onClose={() => setPlayerMoreFilterAnchor(null)}>
        <MenuItem onClick={() => { setPlayerQuickFilter('linked'); setPlayerMoreFilterAnchor(null); }}>TennisRecord linked</MenuItem>
        <MenuItem onClick={() => { setPlayerQuickFilter('not_linked'); setPlayerMoreFilterAnchor(null); }}>TennisRecord not linked</MenuItem>
        <MenuItem onClick={() => { setPlayerQuickFilter('not_assigned'); setPlayerMoreFilterAnchor(null); }}>Not assigned to a team</MenuItem>
      </Menu>
      <Menu anchorEl={playerActionAnchor} open={Boolean(playerActionAnchor)} onClose={closePlayerActionMenu}>
        <MenuItem onClick={() => menuAction((row) => openPlayerDrawer(row))}>View Details</MenuItem>
        {!readOnly && <MenuItem onClick={() => menuAction(openEdit)}>Edit Player</MenuItem>}
        <MenuItem onClick={() => menuAction(openPlayerFinancial)}>Financial History</MenuItem>
        {!readOnly && (
          <MenuItem
            disabled={!selectedActionPlayer || syncingTennisRecordPlayerIds.has(String(selectedActionPlayer.id)) || !tennisRecordLinked(selectedActionPlayer)}
            onClick={() => menuAction(syncPlayerTennisRecord)}
          >
            Sync TennisRecord
          </MenuItem>
        )}
        {!readOnly && <MenuItem onClick={() => menuAction(togglePlayerActive)}>{selectedActionPlayer?.is_active === false ? 'Reactivate' : 'Deactivate'}</MenuItem>}
        {!readOnly && <Divider />}
        {!readOnly && <MenuItem sx={{ color: 'error.main' }} onClick={() => menuAction(softDelete)}>Delete</MenuItem>}
      </Menu>
      {renderPlayerDrawer()}
    </>
  );

  return (
    <Stack spacing={2}>
      <DraftRecoveryDialog
        open={Boolean(formDraftPersistence.recoverableDraft)}
        updatedAt={formDraftPersistence.recoverableDraft?.updatedAt}
        onRestore={() => {
          const restored = formDraftPersistence.restoreDraft();
          if (restored) setForm(restored);
        }}
        onDiscard={formDraftPersistence.discardDraft}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {entity.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {entity.description}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
          {isPlayers && !gridEditMode && !readOnly && (
            <Button variant="outlined" onClick={enterGridEditMode}>
              Edit in Grid
            </Button>
          )}
          {isPlayers && gridEditMode && (
            <>
              <Button variant="outlined" onClick={cancelGridChanges}>
                Cancel
              </Button>
              <Button variant="contained" onClick={() => void saveGridChanges()} disabled={pendingCellCount === 0}>
                Save Changes
              </Button>
            </>
          )}
          {!readOnly && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={gridEditMode}>
              {primaryAddLabel}
            </Button>
          )}
        </Stack>
      </Box>

      {isFacilities && (
        <TextField
          label="Search facilities"
          value={facilitySearch}
          onChange={(event) => setFacilitySearch(event.target.value)}
          placeholder="Search by facility, city, state, or zip"
          sx={{ maxWidth: 460 }}
        />
      )}

      {entity.key === 'players' && gridEditMode && onUserPreferencesChange && (
        <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
      )}

      {isPlayers && gridEditMode && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr repeat(3, 1fr)' }, gap: 2 }}>
              <TextField label="Search players" value={playerSearch} onChange={(event) => setPlayerSearch(event.target.value)} placeholder="Search by name, email, or phone" />
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select label="Gender" value={playerSexFilter} onChange={(event) => setPlayerSexFilter(event.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {sexOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>NTRP</InputLabel>
                <Select label="NTRP" value={playerNtrpFilter} onChange={(event) => setPlayerNtrpFilter(event.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {ntrpOptions.filter(Boolean).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={playerActiveFilter} onChange={(event) => setPlayerActiveFilter(event.target.value)}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {gridEditMode && (
              <Alert severity={pendingCellCount > 0 ? 'warning' : 'info'}>
                {pendingCellCount} pending cell change{pendingCellCount === 1 ? '' : 's'} across {pendingRowCount} player{pendingRowCount === 1 ? '' : 's'}.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      {isPlayers && !gridEditMode ? renderOptimizedPlayersView() : (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        {isPlayers && gridEditMode ? (
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 2250 }}>
            <TableHead>
              <TableRow>
                {playerGridFields.map((field) => (
                  <TableCell key={field} sx={{ fontWeight: 700, minWidth: field === 'notes' ? 260 : field.includes('url') || field === 'email' ? 220 : 140 }}>
                    {playerGridLabels[field]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {renderedRows.map((row) => (
                <TableRow key={String(row.id)} hover>
                  {playerGridFields.map((field) => (
                    <TableCell key={field} sx={{ verticalAlign: 'top' }}>
                      {renderGridInput(row, field)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {renderedRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={playerGridFields.length}>
                    <EmptyState
                      title={rows.length === 0 ? 'No players have been added yet.' : 'No players match these filters.'}
                      message={rows.length === 0 ? undefined : 'Adjust or clear filters to see more players.'}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {entity.columns.map((column) => (
                  <TableCell key={column.name} sx={{ fontWeight: 700 }}>
                    {sortableColumns.has(column.name) ? (
                      <TableSortLabel
                        active={activeSort.field === column.name}
                        direction={activeSort.field === column.name ? activeSort.direction : 'asc'}
                        onClick={() => toggleSort(column.name)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                {!readOnly && (
                  <TableCell align="right" sx={{ fontWeight: 700, minWidth: isPlayers ? 240 : 140 }}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {renderedRows.map((row) => (
                <TableRow key={String(row.id)} hover>
                  {entity.columns.map((column, index) => (
                    <TableCell
                      key={column.name}
                      sx={entity.key === 'teams' && index === 0 ? { borderLeft: `6px solid ${teamAccentColor(row.team_color)}` } : undefined}
                    >
                      {renderCell(row, column.name)}
                    </TableCell>
                  ))}
                  {!readOnly && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
                        {isPlayers && (
                          <Button
                            size="small"
                            startIcon={<SyncIcon />}
                            disabled={syncingTennisRecordPlayerIds.has(String(row.id)) || !profileUrl(row.tennis_record_profile_url)}
                            onClick={() => void syncPlayerTennisRecord(row)}
                          >
                            Sync TennisRecord
                          </Button>
                        )}
                        {isPlayers && (
                          <Button size="small" startIcon={<HistoryIcon />} onClick={() => void openPlayerFinancial(row)}>
                            Financial
                          </Button>
                        )}
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => softDelete(row)}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {renderedRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={entity.columns.length + (readOnly ? 0 : 1)}>
                    <EmptyState
                      title={emptyStateTitle}
                      message={emptyStateMessage}
                      action={!readOnly && rows.length === 0 && !collectionHasActiveFilters ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          {primaryAddLabel}
                        </Button>
                      ) : undefined}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth={isPlayers || isFacilities ? 'lg' : 'md'} fullWidth>
        <DialogTitle>{editing ? `Edit ${singularEntityTitle}` : `Add ${singularEntityTitle}`}</DialogTitle>
        <DialogContent>
          {isPlayers ? renderPlayerForm() : isFacilities ? renderFacilityForm() : renderGenericForm()}
        </DialogContent>
        <DialogActionBar
          onCancel={closeDialog}
          onCommit={() => void save()}
          commitLabel={commitLabel}
          cancelDisabled={saving}
          commitDisabled={readOnly || saving}
          committing={saving}
          destructiveAction={globalWarnings.length > 0 && Object.keys(formErrors).length === 0 && !readOnly ? (
            <Button variant="outlined" color="warning" onClick={() => void save({ acceptWarnings: true })} disabled={saving}>
              Save Anyway
            </Button>
          ) : undefined}
        />
      </Dialog>

      <Dialog open={Boolean(financialPlayer)} onClose={() => setFinancialPlayer(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {financialPlayer ? `${formatPlayerName({ first_name: String(financialPlayer.first_name || ''), last_name: String(financialPlayer.last_name || '') }, userPreferences)} Financial Ledger` : 'Player Financial Ledger'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {financialLoading && <LinearProgress />}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Credit Balance</Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Workspace</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialBalances.map((balance) => (
                      <TableRow key={`${balance.workspace_id}:${balance.player_id}`}>
                        <TableCell>{lookupLabel(lookups, 'workspaces', balance.workspace_id)}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" color={Number(balance.credit_balance || 0) > 0 ? 'success' : 'default'} label={money(Number(balance.credit_balance || 0))} />
                        </TableCell>
                        <TableCell>{balance.last_activity_at ? new Date(balance.last_activity_at).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {financialBalances.length === 0 && !financialLoading && (
                      <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No credit balance recorded for this player.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Ledger Entries</Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Workspace</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialLedger.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                        <TableCell>{lookupLabel(lookups, 'workspaces', entry.workspace_id)}</TableCell>
                        <TableCell>{entry.transaction_type.replaceAll('_', ' ')}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" color={Number(entry.amount) > 0 ? 'success' : 'warning'} label={`${Number(entry.amount) > 0 ? '+' : ''}${money(Number(entry.amount || 0))}`} />
                        </TableCell>
                        <TableCell>{lookupLabel(lookups, 'app_users', entry.created_by_user_id) || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {financialLedger.length === 0 && !financialLoading && (
                      <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No ledger entries for this player.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinancialPlayer(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
